/**
 * @file Leasing utilities.
 */

import moment from 'moment';
import uniqBy from 'lodash.uniqby';

import { LEASE_EXTEND_THRESHOLD_SEC } from '../constants/command';

/**
 * Determines whether a lease is about to expire.
 *
 * @param {Object} lease - The lease.
 * @param {number} lease.expiresAt - The lease's expiration time.
 * @param {number} leaseExtendThreshold - Extend treshhold in secs.
 * @returns {boolean}
 */
const leaseAboutToExpire = (
  lease,
  leaseExtendThreshold = LEASE_EXTEND_THRESHOLD_SEC,
) => {
  const now = moment().unix();

  return now + leaseExtendThreshold >= lease.expiresAt;
};

/**
 * Determines whether a lease has expired.
 *
 * @param {Object} lease - The lease.
 * @param {number} lease.expiresAt - The lease's expiration time.
 * @returns {boolean}
 */
const leaseExpired = lease => {
  const now = moment().unix();

  return now >= lease.expiresAt;
};

/**
 * Returns all leases that are about to expire.
 *
 * @param {Array<Object>} queue - The entire queue.
 * @param {number} leaseExtendThreshold - Extend treshhold in secs.
 * @returns {Array<Object>}
 */
const getExpiringLeases = (
  queue,
  leaseExtendThreshold = LEASE_EXTEND_THRESHOLD_SEC,
) => {
  if (!queue) {
    return [];
  }

  const leases = queue.filter(
    lease =>
      !leaseExpired(lease) && leaseAboutToExpire(lease, leaseExtendThreshold),
  );

  return uniqBy(leases, 'leaseId');
};

/**
 * Extends lease batches.
 *
 * @param {Object} batch - The batch (reducer).
 * @param {Object} action - The reducer action.
 * @returns {Object} - The extended lease batch.
 */
const extendLeaseBatch = (batch, action) => {
  const lease = action.leases.find(l => l.leaseId === batch.leaseId);

  if (lease) {
    return {
      ...batch,
      expiresAt: lease.time,
    };
  }

  return batch;
};

export {
  leaseAboutToExpire,
  leaseExpired,
  getExpiringLeases,
  extendLeaseBatch,
};
